/* Novel Factory Wizard — Admin-only
 *
 * Step 1  Drop a .docx / .txt file  → parse into novel list
 * Step 2  Review + assign genres    → queue for building
 * Step 3  Live progress per novel   → chapters, passes, quality score
 *
 * Genre-pacing detection is automatic; the pacing label is shown as a
 * colour-coded badge alongside each queued novel.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BookOpen, CheckCircle2, ChevronRight, Clock, FileText,
  Loader2, TriangleAlert, Upload, X, Zap, RefreshCw,
} from 'lucide-react';
import { novelFactoryApi } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// ── Pacing badge colours (must mirror backend _GENRE_KEYWORDS order) ──────────
const PACING_COLOURS = {
  dark_romance:  { bg: '#2a0510', border: '#c0394a', text: '#f0b0ba', label: 'Fast · Peril' },
  romance:       { bg: '#0e0818', border: '#9b68d8', text: '#d4b8f5', label: 'Slow Burn'   },
  mystery:       { bg: '#04111a', border: '#2b7ab3', text: '#a3d1f5', label: 'Fast · Reveal'},
  adventure:     { bg: '#0d1a04', border: '#4a9e2b', text: '#b3e89b', label: 'Fast · Action'},
  scifi:         { bg: '#020d1a', border: '#1d8fb5', text: '#87ddf7', label: 'Fast · Multi-Arc'},
  horror:        { bg: '#0d0d04', border: '#8b8b1a', text: '#e8e8a0', label: 'Atmospheric' },
  fantasy:       { bg: '#0a0418', border: '#7b5ea0', text: '#c9b8e8', label: 'Epic'         },
  default:       { bg: '#111111', border: '#555555', text: '#cccccc', label: 'Standard'      },
};

function detectPacingKey(genre) {
  const g = (genre || '').toLowerCase();
  if (/dark romance|dark-romance|captive/.test(g)) return 'dark_romance';
  if (/romance|slow burn|slow-burn/.test(g))       return 'romance';
  if (/mystery|thriller|crime|detective|noir/.test(g)) return 'mystery';
  if (/adventure|action|quest/.test(g))            return 'adventure';
  if (/sci-?fi|science fiction|space opera|cyberpunk|dystopian|military sci/.test(g)) return 'scifi';
  if (/horror|gothic/.test(g))                     return 'horror';
  if (/fantasy/.test(g))                           return 'fantasy';
  return 'default';
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_META = {
  queued:          { icon: Clock,         colour: '#888',    label: 'Queued'             },
  creating:        { icon: Loader2,       colour: '#60a5fa', label: 'Creating project'   },
  drafting_pass1:  { icon: Zap,           colour: '#a78bfa', label: 'Sonnet drafting…'   },
  scoring:         { icon: RefreshCw,     colour: '#fbbf24', label: 'Scoring…'           },
  editing_pass2:   { icon: BookOpen,      colour: '#34d399', label: 'Opus editing pass 2…'},
  editing_pass3:   { icon: BookOpen,      colour: '#34d399', label: 'Opus editing pass 3…'},
  completed:       { icon: CheckCircle2,  colour: '#4ade80', label: 'Complete'           },
  best_effort:     { icon: TriangleAlert, colour: '#fbbf24', label: 'Best effort'        },
  failed:          { icon: TriangleAlert, colour: '#f87171', label: 'Failed'             },
  cancelled:       { icon: X,            colour: '#888',    label: 'Cancelled'          },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.queued;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ background: `${m.colour}22`, color: m.colour, border: `1px solid ${m.colour}55` }}>
      <Icon className="h-3 w-3" style={{ color: m.colour }} />
      {m.label}
    </span>
  );
}

function PacingBadge({ genre }) {
  const key = detectPacingKey(genre);
  const c   = PACING_COLOURS[key] || PACING_COLOURS.default;
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide"
          style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {c.label}
    </span>
  );
}

function ScorePill({ score, label }) {
  if (score == null) return null;
  const colour = score >= 80 ? '#4ade80' : score >= 70 ? '#fbbf24' : '#f87171';
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold"
          style={{ background: `${colour}22`, color: colour, border: `1px solid ${colour}44` }}>
      {score}% · {label || (score >= 80 ? 'Publishable' : 'Review needed')}
    </span>
  );
}

// ── Step 1 — Drop zone ────────────────────────────────────────────────────────
function DropZone({ onParsed, onError }) {
  const [dragging, setDragging] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const inputRef = useRef(null);

  const handle = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['docx', 'txt', 'md'].includes(ext)) {
      onError('Only .docx, .txt, or .md files are supported.');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const data = await novelFactoryApi.parseFile(fd);
      onParsed(data.novels || []);
    } catch (e) {
      onError(e?.response?.data?.detail || 'Could not parse the file. Check the format and try again.');
    } finally {
      setLoading(false);
    }
  }, [onParsed, onError]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true);  }}
      onDragLeave={()  => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files[0]); }}
      onClick={() => !loading && inputRef.current?.click()}
      data-testid="novel-factory-dropzone"
      className="flex cursor-pointer flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-12 transition-colors"
      style={{
        borderColor: dragging ? '#c0394a' : '#333',
        background:  dragging ? 'rgba(192,57,74,0.08)' : 'rgba(255,255,255,0.02)',
      }}
    >
      <input ref={inputRef} type="file" accept=".docx,.txt,.md" className="hidden"
             onChange={(e) => handle(e.target.files[0])} />
      {loading
        ? <Loader2 className="h-10 w-10 animate-spin text-[#c0394a]" />
        : <Upload  className="h-10 w-10" style={{ color: dragging ? '#c0394a' : '#666' }} />
      }
      <div className="text-center">
        <p className="text-sm font-semibold text-white/80">
          {loading ? 'Parsing file…' : 'Drop your synopsis file here'}
        </p>
        <p className="mt-1 text-xs text-white/40">
          .docx · .txt · .md — title line then synopsis, blank line between entries
        </p>
      </div>
    </div>
  );
}

// ── Step 2 — Review list ──────────────────────────────────────────────────────
const GENRE_OPTIONS = [
  'Fiction', 'Adventure', 'Mystery', 'Thriller',
  'Romance', 'Slow Burn Romance', 'Dark Romance',
  'Science Fiction', 'Fantasy', 'Epic Fantasy',
  'Gothic Horror', 'Horror', 'Literary Fiction',
];

function ReviewList({ novels, onQueue, onBack }) {
  const [items, setItems] = useState(() =>
    novels.map((n) => ({ ...n, genre: n.genre || 'Fiction' })),
  );
  const [wpc,     setWpc]     = useState(2500);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const update = (idx, field, val) =>
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const handleQueue = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await novelFactoryApi.queue({ items, words_per_chapter: wpc });
      onQueue(data.run_id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not queue the run. Try again.');
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="novel-factory-review">
      {/* WPC global setting */}
      <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/4 p-3">
        <FileText className="h-4 w-4 flex-none text-white/40" />
        <span className="text-[12px] text-white/60">Words per chapter (base)</span>
        <select
          value={wpc}
          onChange={(e) => setWpc(Number(e.target.value))}
          className="ml-auto rounded border border-white/20 bg-[#0e0e0e] px-2 py-1 text-[12px] text-white"
          data-testid="novel-factory-wpc-select"
        >
          {[1500,2000,2500,3000,3500,4000].map((v) => (
            <option key={v} value={v}>{v.toLocaleString()} words</option>
          ))}
        </select>
        <span className="text-[10px] text-white/30">genre pacing may adjust this</span>
      </div>

      {/* Novel list */}
      <div className="max-h-[52vh] space-y-2.5 overflow-y-auto pr-1">
        {items.map((it, idx) => (
          <div key={idx}
               className="rounded-lg border border-white/10 bg-white/3 p-3.5"
               data-testid={`novel-factory-item-${idx}`}>
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/8 text-[9.5px] font-bold text-white/50">
                {idx + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                <input
                  value={it.title}
                  onChange={(e) => update(idx, 'title', e.target.value)}
                  placeholder="Title"
                  className="w-full rounded border border-white/10 bg-transparent px-2 py-1 text-[13px] font-semibold text-white placeholder-white/25 focus:outline-none focus:border-white/30"
                />
                <div className="flex items-center gap-2">
                  <select
                    value={it.genre}
                    onChange={(e) => update(idx, 'genre', e.target.value)}
                    className="rounded border border-white/10 bg-[#0e0e0e] px-2 py-1 text-[11.5px] text-white/80"
                  >
                    {GENRE_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <PacingBadge genre={it.genre} />
                </div>
                {it.synopsis && (
                  <p className="line-clamp-2 text-[11px] text-white/40 leading-relaxed">
                    {it.synopsis}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p className="rounded-lg bg-red-900/30 px-3 py-2 text-[12px] text-red-400">{error}</p>
      )}

      <div className="flex items-center justify-between pt-1">
        <button onClick={onBack} className="text-[12px] text-white/40 hover:text-white/70 transition-colors">
          ← Change file
        </button>
        <button
          onClick={handleQueue}
          disabled={loading}
          data-testid="novel-factory-queue-btn"
          className="flex items-center gap-2 rounded-lg px-5 py-2 text-[13px] font-bold text-white transition-all hover:brightness-110 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #c0394a, #8b1a28)' }}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          {loading ? 'Queuing…' : `Build ${items.length} novel${items.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}

// ── Step 3 — Live progress ────────────────────────────────────────────────────
function ProgressView({ runId, onClose, onNewRun }) {
  const [run,      setRun]      = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const intervalRef = useRef(null);

  const fetch = useCallback(async () => {
    try {
      const data = await novelFactoryApi.getRun(runId);
      setRun(data);
      if (['completed', 'cancelled'].includes(data.status)) {
        clearInterval(intervalRef.current);
      }
    } catch { /* ignore */ }
  }, [runId]);

  useEffect(() => {
    fetch();
    intervalRef.current = setInterval(fetch, 4000);
    return () => clearInterval(intervalRef.current);
  }, [fetch]);

  const cancel = async () => {
    setCancelling(true);
    try { await novelFactoryApi.cancelRun(runId); } catch { /* ignore */ }
    setCancelling(false);
  };

  if (!run) return (
    <div className="flex items-center justify-center py-16" data-testid="novel-factory-progress-loading">
      <Loader2 className="h-8 w-8 animate-spin text-white/30" />
    </div>
  );

  const done       = run.items.filter((it) => ['completed','best_effort'].includes(it.status)).length;
  const totalItems = run.items.length;
  const pct        = totalItems > 0 ? Math.round((done / totalItems) * 100) : 0;

  return (
    <div className="flex flex-col gap-4" data-testid="novel-factory-progress">
      {/* Summary bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11.5px]">
          <span className="font-semibold text-white/80">
            {done} / {totalItems} complete · {run.totals?.words_total?.toLocaleString() || 0} words written
          </span>
          <StatusBadge status={run.status} />
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/8">
          <div className="h-full rounded-full bg-gradient-to-r from-[#c0394a] to-[#e8527a] transition-all duration-700"
               style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Per-novel rows */}
      <div className="max-h-[56vh] space-y-2 overflow-y-auto pr-1">
        {run.items.map((it, idx) => (
          <div key={idx}
               className="rounded-lg border border-white/8 bg-white/3 px-3.5 py-3"
               data-testid={`novel-factory-progress-item-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-white/90 truncate max-w-[240px]">
                    {it.title}
                  </span>
                  <PacingBadge genre={it.genre} />
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <StatusBadge status={it.status} />
                  {it.quality_score != null && (
                    <ScorePill score={it.quality_score} label={it.quality_label} />
                  )}
                  {it.effective_wpc && (
                    <span className="text-[10px] text-white/35">
                      {it.effective_wpc.toLocaleString()} wpc
                    </span>
                  )}
                </div>
                {/* Chapter progress bar */}
                {it.chapters_done > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/8">
                      <div className="h-full rounded-full bg-[#a78bfa]/60 transition-all duration-500"
                           style={{ width: `${Math.min(100, Math.round((it.chapters_done / 15) * 100))}%` }} />
                    </div>
                    <span className="text-[9.5px] text-white/30 tabular-nums">
                      ch {it.chapters_done}/15
                    </span>
                  </div>
                )}
                {it.score_notes && (
                  <p className="mt-1 text-[10px] italic text-white/30 line-clamp-1">{it.score_notes}</p>
                )}
                {it.error && (
                  <p className="mt-1 text-[10px] text-red-400/80 line-clamp-2">{it.error}</p>
                )}
                {it.project_id && ['completed','best_effort'].includes(it.status) && (
                  <a
                    href={`/app?project=${it.project_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-[#c0394a] hover:underline"
                    data-testid={`novel-factory-open-project-${idx}`}
                  >
                    Open in editor <ChevronRight className="h-3 w-3" />
                  </a>
                )}
              </div>
              {/* Pass score pills */}
              {it.pass_scores?.length > 0 && (
                <div className="flex flex-col items-end gap-1 flex-none">
                  {it.pass_scores.map((s, pi) => (
                    <span key={pi} className="text-[9.5px] text-white/30 tabular-nums">
                      Pass {pi + 1}: {s}%
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between border-t border-white/8 pt-3">
        {run.status === 'running' ? (
          <button
            onClick={cancel}
            disabled={cancelling}
            className="text-[11.5px] text-red-400/60 hover:text-red-400 transition-colors"
            data-testid="novel-factory-cancel-btn"
          >
            {cancelling ? 'Cancelling…' : 'Cancel run'}
          </button>
        ) : (
          <button onClick={onNewRun}
                  className="text-[11.5px] text-white/40 hover:text-white/70 transition-colors">
            ← New run
          </button>
        )}
        <button
          onClick={onClose}
          className="rounded-lg bg-white/8 px-4 py-1.5 text-[12px] font-medium text-white/70 hover:bg-white/12 transition-colors"
          data-testid="novel-factory-close-btn"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── Root wizard ───────────────────────────────────────────────────────────────
export default function NovelFactoryWizard({ open, onClose }) {
  const [step,   setStep]   = useState('drop');  // 'drop' | 'review' | 'progress'
  const [novels, setNovels] = useState([]);
  const [runId,  setRunId]  = useState(null);
  const [error,  setError]  = useState('');

  const reset = () => { setStep('drop'); setNovels([]); setRunId(null); setError(''); };

  const handleClose = () => { reset(); onClose?.(); };

  const STEP_TITLES = {
    drop:     'Novel Factory',
    review:   `Review ${novels.length} novel${novels.length !== 1 ? 's' : ''}`,
    progress: 'Building manuscripts…',
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-2xl gap-0 p-0 overflow-hidden border-0"
        style={{ background: '#0a0a0a', boxShadow: '0 32px 80px -20px rgba(0,0,0,0.8)' }}
        data-testid="novel-factory-wizard"
      >
        {/* Header */}
        <DialogHeader className="border-b border-white/8 px-5 py-4"
                       style={{ background: 'linear-gradient(135deg, #1a0408 0%, #0d0205 100%)' }}>
          <DialogTitle className="flex items-center gap-2.5 text-[14.5px] font-bold text-white">
            <Zap className="h-4 w-4 text-[#c0394a]" />
            {STEP_TITLES[step]}
          </DialogTitle>
          {step === 'drop' && (
            <p className="mt-0.5 text-[11px] text-white/40">
              Drop a .docx or .txt file · Title line + synopsis · blank line between novels
            </p>
          )}
          {step === 'progress' && runId && (
            <p className="mt-0.5 text-[11px] text-white/40">
              Run ID: <code className="text-white/60">{runId}</code>
            </p>
          )}
        </DialogHeader>

        {/* Body */}
        <div className="px-5 py-5">
          {step === 'drop' && (
            <>
              <DropZone
                onParsed={(list) => { setNovels(list); setStep('review'); setError(''); }}
                onError={(msg)  => setError(msg)}
              />
              {error && (
                <p className="mt-3 rounded-lg bg-red-900/25 px-3 py-2 text-[12px] text-red-400">
                  {error}
                </p>
              )}

              {/* Recent runs shortcut */}
              <RecentRuns onSelect={(id) => { setRunId(id); setStep('progress'); }} />
            </>
          )}

          {step === 'review' && novels.length > 0 && (
            <ReviewList
              novels={novels}
              onQueue={(id) => { setRunId(id); setStep('progress'); }}
              onBack={() => setStep('drop')}
            />
          )}

          {step === 'progress' && runId && (
            <ProgressView
              runId={runId}
              onClose={handleClose}
              onNewRun={reset}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Recent runs shortcut ──────────────────────────────────────────────────────
function RecentRuns({ onSelect }) {
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    novelFactoryApi.listRuns()
      .then((d) => setRuns((d.runs || []).slice(0, 5)))
      .catch(() => {});
  }, []);

  if (!runs.length) return null;

  return (
    <div className="mt-4 space-y-1.5">
      <p className="text-[10.5px] uppercase tracking-wider text-white/30">Recent runs</p>
      {runs.map((r) => (
        <button
          key={r.id}
          onClick={() => onSelect(r.id)}
          className="flex w-full items-center justify-between rounded-lg border border-white/8 bg-white/3 px-3 py-2 text-left hover:bg-white/6 transition-colors"
          data-testid={`novel-factory-recent-run-${r.id}`}
        >
          <span className="text-[11.5px] text-white/70">
            {r.totals?.total || 0} novels · {new Date(r.created_at).toLocaleDateString()}
          </span>
          <StatusBadge status={r.status} />
        </button>
      ))}
    </div>
  );
}
